import { authenticationService, User } from '../../services/authentication';
import * as React from 'react';
import { LoginValidation } from '../../models/user/validation';
import { PageRoutes } from '../root/Routes';
import { EventManager } from '../../services/EventManager';
import { LogManager } from '../../utils/LogManager';
import { beautifyMessageIfError, bindObjectMethods } from '../../utils/utilFunctions';
import { WebportalEvent } from '../../services/WebportalEvent';
import './authentication.css';
import {
    CognitoException, CognitoGenericExceptionMessage,
    CognitoResetPasswordExceptionMessage, ResendVerificationCodeComponent
} from './errors/CognitoErrors';
import {
    DEFAULT_ERROR_MESSAGE,
    DONT_HAVE_AN_ACCOUNT_YET_MESSAGE,
    GOOGLE_IDENTITY_PROVIDER,
    SIGN_UP_MESSAGE, UNCONFIRMED_USER_SESSION_STORAGE_KEY
} from '../../utils/constants';
import { AuthenticationFooter } from '../common/authenticationCardComponents/AuthenticationFooter';
import { CardHeader } from '../common/authenticationCardComponents/CardHeader';
import { SocialButton } from '../common/authenticationCardComponents/SocialButton';
import { EnvelopeIcon, LockIcon } from '../common/Icons';
import { LucidLinkLogo } from '../common/Icons';
import { LUCIDLINK_WEBSITE } from '../../utils/constants';
import { AuthenticationFooterSecondary } from '../common/authenticationCardComponents/AuthenticationFooterSecondary';

const logManager = new LogManager('Login');

const DEFAULT_VALIDATION = {
    emailIsValid: false,
    passwordIsValid: false,
    generalValidationMessage: '',
    passwordValidationMessage: ''
};

interface LoginState {
    user: User;
    loginFor14Days: boolean;
    showPassword: boolean;
    validation: LoginValidation;
    buttonDisabled: boolean;
    loading: boolean;
    showValidationErrors: boolean;
    isEmailInputFieldPristine: boolean;
    isPasswordInputFieldPristine: boolean;
}

class Login extends React.Component<any, LoginState> {
    private passwordRef = React.createRef<HTMLInputElement>();

    constructor(props: any) {
        super(props);

        this.state = {
            user: { ...Object.assign({}, new User('', '')) },
            buttonDisabled: true,
            showPassword: false,
            validation: { ...Object.assign({}, DEFAULT_VALIDATION) },
            loginFor14Days: false,
            loading: true,
            showValidationErrors: false,
            isEmailInputFieldPristine: true,
            isPasswordInputFieldPristine: true,
        };

        bindObjectMethods(this, Login.prototype);
    }

    componentDidMount() {
        EventManager.fireCustomEvent(WebportalEvent.FloatLabels);

        this.clearValidation();

        let emailFromQuery = this.getEmailFromQuery();
        const unconfirmedUser = this.props.location.props?.email || emailFromQuery;
        if (unconfirmedUser) {
            const user = new User(unconfirmedUser, '');

            this.setState({ user }, () => {
                if (this.state.user?.email.length > 0) {
                    this.passwordRef.current?.focus();
                }
            });
        } else {
            const emailToReset = this.props.history.location.state?.email;
            if (emailToReset) {
                let user = new User(emailToReset, '');

                this.setState({ user }, () => {
                    if (this.state.user?.email.length > 0) {
                        this.passwordRef.current?.focus();
                    }
                });

                this.props.history.push(PageRoutes.Login);
            }
        }

        this.setState({ loading: false });
    }

    render() {
        const redirectMessage = (this.props && this.props.location && this.props.location.props) ? this.props.location.props.message : null;
        return (
            <div className="auth">
                <div className="auth_wrapper">
                    <header>
                        <a href={LUCIDLINK_WEBSITE}>
                            <LucidLinkLogo width={'130px'} height={'50px'}/>
                        </a>
                    </header>
                    <div className={redirectMessage ? 'auth_block auth_success_msg' : 'auth_block'}>
                        <div>
                            <CardHeader message="Welcome back!" redirectMessage={redirectMessage}/>
                            <div className="auth_form">
                                <SocialButton identityProvider={GOOGLE_IDENTITY_PROVIDER} text={'Sign in with Google'}/>
                                <p>
                                    or sign in with
                                </p>
                                <div
                                    className={
                                    this.state.isEmailInputFieldPristine && 'float-label-control' ||
                                    this.state.loading ? 'float-label-control' : 'float-label-control has-danger'
                                }
                                >
                                    <input
                                        type="email"
                                        placeholder="E-mail address"
                                        className="form-control"
                                        autoFocus={true}
                                        name="email"
                                        id="email"
                                        value={(this.state.user.email || this.props.history.location.state?.email) || ''}
                                        onChange={event => this.handleInputChange(event)}
                                        onKeyDown={(event) => this.handleEnter(event)}
                                    />
                                    <label>E-mail address</label>
                                    <span className="icon">
                                        <EnvelopeIcon
                                            width={'17px'}
                                            height={'18px'}
                                            fill={
                                                (this.state.isEmailInputFieldPristine && 'black') ||
                                                (this.state.validation.emailIsValid || this.state.loading ? '#9DC53B' : '#F40000')
                                        }
                                        />
                                    </span>
                                    <div className="row">
                                        <span className="minitext pt-2">{this.state.showValidationErrors && this.state.validation.generalValidationMessage}</span>
                                    </div>
                                </div>
                                <div
                                    className={
                                    this.state.isPasswordInputFieldPristine && 'float-label-control' ||
                                     this.state.loading ? 'float-label-control' : 'float-label-control has-danger'
                                }
                                >
                                    <input
                                        placeholder="Password"
                                        className="form-control"
                                        name="password"
                                        id="password"
                                        ref={this.passwordRef}
                                        type={this.state.showPassword ? 'input' : 'password'}
                                        value={this.state.user.password || ''}
                                        onChange={event => this.handleInputChange(event)}
                                        onKeyDown={(event) => this.handleEnter(event)}
                                    />
                                    <label>Password</label>
                                    <span className="icon">
                                        <LockIcon
                                            width={'17px'}
                                            height={'19px'}
                                            fill={
                                                (this.state.isPasswordInputFieldPristine && 'black') ||
                                                (this.state.validation.passwordIsValid || this.state.loading ? '#9DC53B' : '#F40000')}
                                        />
                                    </span>
                                    <div className="row">
                                        <span className="minitext pt-2">{this.state.showValidationErrors && this.state.validation.passwordValidationMessage}</span>
                                        <span className="minitext pt-2">
                                        <a id="forgottenPassword" href="#" tabIndex={-1} onClick={this.onForgotPasswordClicked}>Forgot password</a>
                                    </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                className="btn"
                                disabled={this.state.loading}
                                onClick={this.onSubmit}
                            >
                                <span>{this.state.loading ? 'Please wait...' : 'Sign in'}</span>
                            </button>
                            <AuthenticationFooter message={{ text: DONT_HAVE_AN_ACCOUNT_YET_MESSAGE, label: SIGN_UP_MESSAGE }} />
                        </div>
                    </div>
                    <AuthenticationFooterSecondary linkHref="https://app.lucidlink.com/login" linkLabel="Log in to LucidLink" />
                </div>
            </div>
        );
    }

    private handleEnter(event: any) {
        const ENTER_KEYCODE = 13;
        if (event.keyCode === ENTER_KEYCODE) {
            this.submit();
        }
    }

    private async onForgotPasswordClicked(
        event: React.MouseEvent<HTMLAnchorElement>
    ): Promise<void> {
        this.setState({ loading: true, showValidationErrors: false });
        event.preventDefault();

        const emailValid = this.getEmailValidation(this.state.validation);
        if (!emailValid.emailIsValid) {
            this.setState({
                showValidationErrors: true,
                loading: false
            });

            return;
        }

        try {
            await authenticationService.resetPassword(
                this.state.user.email || this.props.history.location.state?.email,
                this.inputVerificationCode
            );
        } catch (error) {
            this.setState({
              isEmailInputFieldPristine: false,
              isPasswordInputFieldPristine: false,
            });

            logManager.error(error);
            this.onResetPasswordError(error);
        }
    }

    private onResetPasswordError(err: any): void {
        let validationMessage: string | JSX.Element = '';
        switch (err.code) {
            case CognitoException.NotAuthorizedException:
            case CognitoException.UserNotFoundException:
                validationMessage = CognitoGenericExceptionMessage.AccountInvalid;
                break;
            case CognitoException.LimitExceededException:
                validationMessage = CognitoResetPasswordExceptionMessage.LimitExceeded;
                break;
            case CognitoException.UserNotConfirmedException:
                validationMessage = <ResendVerificationCodeComponent resendCode={this.resendCode} />;
                break;
            case CognitoException.InvalidParameterException:
                validationMessage = CognitoGenericExceptionMessage.InvalidParameter;
                break;
            default:
                validationMessage = DEFAULT_ERROR_MESSAGE;
                break;
        }

        this.setState(prevState => {
            let validation = prevState.validation;
            validation.generalValidationMessage = validationMessage;
            return {
                validation,
                showValidationErrors: true,
                loading: false
            };
        });
    }

    private inputVerificationCode(): void {
        this.setState({ loading: false });
        this.props.history.push({
            pathname: PageRoutes.ResetPassword,
            props: {
                userToReset:
                    this.state.user.email || this.props.history.location.state?.email
            }
        });
    }

    private handleInputChange(event: any): void {
        const field = event.target.name;
        const value = event.target.value.trim();
        this.setState(
            (prevState => {
                let user = prevState.user;
                user[field] = value;
                return { user: { ...user } };
            })
        );
    }

    private getIsFormValid(): boolean {
        const validation = this.state.validation;
        const emailValidation = this.getEmailValidation(validation);
        const passwordValidation = this.getPasswordValidation(emailValidation);
        return emailValidation.emailIsValid && passwordValidation.passwordIsValid;
    }
    private getEmailValidation(priorValidation: LoginValidation): LoginValidation {
        const user = this.state.user;
        let validation = { ...priorValidation };

        let emailValid = user.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i) ? true : false;
        if (user.email === '' || !emailValid) {
            validation.emailIsValid = false;
            validation.generalValidationMessage = 'Enter a valid email address.';
            this.setState({showValidationErrors: true, isEmailInputFieldPristine: false, validation: validation});
        } else {
            validation.emailIsValid = true;
            validation.generalValidationMessage = '';
            this.setState({isEmailInputFieldPristine: false});
        }
        return validation;
    }

    private getPasswordValidation(priorValidation: LoginValidation): LoginValidation {
        const user = this.state.user;
        let validation = { ...priorValidation };

        if (user.password.length !== 0) {
            validation.passwordIsValid = true;
            validation.passwordValidationMessage = '';
            this.setState({showValidationErrors: false, isPasswordInputFieldPristine: false, validation: validation});

        } else {
            validation.passwordValidationMessage = 'Please enter your password.';
            validation.passwordIsValid = false;
            this.setState({showValidationErrors: true, isPasswordInputFieldPristine: false, validation: validation});
        }

        return validation;
    }

    private async submit(): Promise<void> {
        this.setState({ showValidationErrors: false });

        if (!this.getIsFormValid() || this.state.loading) {
            this.setState({ showValidationErrors: true });

            return;
        }

        this.setState({ loading: true });
        this.clearValidation();

        if (this.getIsFormValid()) {
            try {
                await authenticationService.login(this.state.user);
                this.onSuccessfulLogin();
            } catch (error) {
                this.onFailedLogin(error);
            }
        }
    }

    private onSubmit(event: any): void {
        event.preventDefault();
        this.submit();
    }

    private onSuccessfulLogin(): void {
        logManager.log(
            `Login succeeded for ${authenticationService.getLastKnownUser()} (Cognito)`,
            true
        );
        localStorage.removeItem(UNCONFIRMED_USER_SESSION_STORAGE_KEY);
        this.clearValidation();
        this.setState({ loading: false });
        this.props.history.push(PageRoutes.Dashboard);

        if (localStorage.getItem('resetUser')) {
            localStorage.removeItem('resetUser');
        }
    }

    private onFailedLogin(error: any): void {
        logManager.error(
            `Login failed for ${this.state.user.email} (Cognito)${beautifyMessageIfError(error)}`
        );
        let validationMessage: string | JSX.Element = '';
        switch (error.code) {
            case CognitoException.NotAuthorizedException:
            case CognitoException.UserNotFoundException:
                validationMessage = CognitoGenericExceptionMessage.AccountInvalid;
                break;
            case CognitoException.UserNotConfirmedException:
                validationMessage = <ResendVerificationCodeComponent resendCode={this.resendCode}/>;
                break;
            default:
                validationMessage = DEFAULT_ERROR_MESSAGE;
                break;
        }

        this.setState(prevState => {
            let validation = prevState.validation;
            validation.generalValidationMessage = validationMessage;
            return {
                validation,
                showValidationErrors: true,
                loading: false
            };
        });
    }

    private async resendCode(): Promise<void> {
        try {
            await authenticationService.resendCode(this.state.user.email);
            localStorage.setItem(UNCONFIRMED_USER_SESSION_STORAGE_KEY, this.state.user.email);
            this.props.history.push(PageRoutes.CodeConfirmation);
        } catch (error) {
            logManager.error(error);
            this.setState(prevState => {
                let validation = prevState.validation;
                validation.generalValidationMessage = 'Something went wrong.';
                return {
                    validation,
                    showValidationErrors: true,
                };
            });
        }
    }

    private clearValidation(): void {
        this.setState({ validation: Object.assign({}, DEFAULT_VALIDATION) });
    }

    private getEmailFromQuery(): string {
        const searchParams = new URLSearchParams(document.location.search).get('confirmedEmail');
        if (!searchParams) {
            return '';
        }

        return searchParams;
    }

}

export default Login;



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/Login.tsx